import React, { Component } from "react";
import PropTypes from "prop-types";
import { createBrowserHistory } from "history";
import { Route, Router } from "react-router-dom";
import Loading from "../shared/loading/Loading";
import AccessDeniedAsBevDisabled from "../components/accessDenied/AccessDeniedAsBevDisabled";
import CRCApp from "./CRCApp";
import NADealerApp from "./NADealerApp";
import { HAS_EMP_ONBOARD_ROLE, IS_CANADA_MARKET } from "../utils/EmpUtil";
import {
  AGREEMENT_ACCEPTED_STATUS,
  isCanadianPreorderUser,
} from "../utils/canadianPreorderExperience";
import FieldViewApp from "./FieldViewApp";
import NSCApp from "./NSCApp";
import EcommDealerOnboardingApp from "./EcommDealerOnboardingApp";
import EnrollmentRequired from "../components/accessDenied/EnrollmentRequired";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { initDynatrace } from "../shared/services/dynatrace/DynatraceService";
import ErrorBoundary from "../errors/ErrorBoundary";
import { UserProvider } from "../shared/context/userContext";
import { initI18n } from "../i18n";

class App extends Component {
  static loadingCount = 0;

  static hideOrShow(show, setZIndex = false) {
    const loadingEl = document.getElementById("loading");
    const wrapperEl = document.getElementById("appWrapper");

    if (show) {
      App.loadingCount += 1;
      if (loadingEl) loadingEl.style.display = "block";
      if (wrapperEl && setZIndex) wrapperEl.style.zIndex = "-1";
      return;
    }

    if (App.loadingCount === 1) {
      if (loadingEl) loadingEl.style.display = "none";
      if (
        wrapperEl &&
        (wrapperEl.style.zIndex === "-1" || wrapperEl.style.zIndex === -1)
      ) {
        wrapperEl.style.zIndex = "auto";
      }
    }
    if (App.loadingCount > 0) App.loadingCount -= 1;
  }

  constructor(props) {
    super(props);
    this.state = {
      renderApp: false,
    };
    App.loadingCount = 0;

    App.hideOrShow = App.hideOrShow.bind(this);
  }

  componentDidMount() {
    // A reload or post-login restore of /error would show a stale error, so retry from the root.
    if (/^\/error\/?$/.test(globalThis.location.pathname)) {
      globalThis.history.replaceState(null, "", "/");
    }

    initDynatrace();
    this.userDetails = this.props.userDetails;
    initI18n();
    this.setState({ renderApp: true });
  }

  isNsc = (userDetails) => userDetails.userType === "nsc";

  isCrc = (userDetails) => userDetails.userType === "crc";

  isNADealer = (userDetails) => userDetails.userType === "na_dealer";

  isCanadianPreOrderDealer = (userDetails) =>
    (this.isNADealer(userDetails) ||
      this.isEcommDealerOnboarding(userDetails)) &&
    IS_CANADA_MARKET(userDetails.market) &&
    HAS_EMP_ONBOARD_ROLE(userDetails);

  isFieldView = (userDetails) => userDetails.userType === "field_view";

  isEcommDealerOnboarding = (userDetails) =>
    userDetails.userType === "ecomm_onboarding";

  isNoAccess = (userDetails) => {
    if (!userDetails) {
      return false;
    }
    const { userType, permissions, roles } = userDetails;
    const hasNoRolePermissions =
      !permissions || permissions.rolePermissions == null;
    const hasNoVehiclePermissions =
      !permissions || permissions.vehiclePermissions == null;
    const hasNoRoles = !Array.isArray(roles) || roles.length === 0;

    return (
      userType === "no_access" ||
      hasNoRolePermissions ||
      hasNoVehiclePermissions ||
      hasNoRoles
    );
  };

  render() {
    if (!this.props.token || !this.props.userDetails) {
      return <Loading />;
    }

    return this.state.renderApp ? (
      <QueryClientProvider
        client={
          new QueryClient({
            defaultOptions: {
              queries: {
                refetchOnWindowFocus: false,
              },
            },
          })
        }
      >
        <UserProvider>
          <ErrorBoundary>
            <Router history={createBrowserHistory()}>
              <Loading brand={this.userDetails.brand} />
              <Route
                render={(props) => {
                  if (
                    this.isNADealer(this.userDetails) ||
                    isCanadianPreorderUser(
                      this.userDetails,
                      AGREEMENT_ACCEPTED_STATUS
                    ) ||
                    this.isCanadianPreOrderDealer(this.userDetails)
                  ) {
                    return (
                      <NADealerApp
                        {...props}
                        userDetails={this.userDetails}
                        hideOrShow={App.hideOrShow}
                      />
                    );
                  } else if (
                    this.isNsc(this.userDetails) &&
                    IS_CANADA_MARKET(this.userDetails.market)
                  ) {
                    return (
                      <NSCApp
                        {...props}
                        isNsc={true}
                        i18n={this.i18n}
                        userDetails={this.userDetails}
                        hideOrShow={App.hideOrShow}
                      />
                    );
                  } else if (this.isCrc(this.userDetails)) {
                    return (
                      <CRCApp
                        {...props}
                        hideOrShow={App.hideOrShow}
                        i18n={this.i18n}
                        userDetails={this.userDetails}
                      />
                    );
                  } else if (this.isFieldView(this.userDetails)) {
                    return (
                      <FieldViewApp
                        {...props}
                        i18n={this.i18n}
                        userDetails={this.userDetails}
                        hideOrShow={App.hideOrShow}
                      />
                    );
                  } else if (this.isEcommDealerOnboarding(this.userDetails)) {
                    return (
                      <EcommDealerOnboardingApp
                        {...props}
                        i18n={this.i18n}
                        userDetails={this.userDetails}
                        hideOrShow={App.hideOrShow}
                      />
                    );
                  } else if (this.isNoAccess(this.userDetails)) {
                    return (
                      <EnrollmentRequired
                        {...props}
                        userDetails={this.userDetails}
                      />
                    );
                  } else {
                    if (document.getElementById("loading")) {
                      return (
                        <AccessDeniedAsBevDisabled
                          {...props}
                          userDetails={this.userDetails}
                        />
                      );
                    } else {
                      return null;
                    }
                  }
                }}
              />
            </Router>
          </ErrorBoundary>
        </UserProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    ) : (
      <Loading />
    );
  }
}

App.propTypes = {
  token: PropTypes.string,
  userDetails: PropTypes.object,
};

export default App;
